import { create } from "zustand";
import { RISK_FRESH_MS, riToLevel, type Level } from "@/shared/lib/levelTone";
import type { RiskGasDetail, RiskLatest, RiskUpdatedPayload } from "@/shared/api/models";
import { normGas, topContributors } from "./nodeRisk";

/**
 * liveRiskStore — 관제 화면의 **노드별 실시간 위험도** 한 곳 (2026-09-29 관제 개편 P0).
 *
 * 예전 관제 화면은 `risk.updated` 소켓을 듣지 않고 표본 목록을 30초마다 다시 읽었으며,
 * 그 목록의 맨 앞 1건(= 마지막으로 보고한 아무 노드)을 현장 RI 로 썼다. 그래서 헤드라인이
 * 노드 사이를 오갔고, 훈련 표본은 목록에서 빠져 시뮬레이터를 돌려도 「0.05」가 움직이지 않았다.
 *
 * 여기서는 노드마다 프레임을 따로 들고(SiteRiskBridge 가 소켓과 `latest` 폴링을 넣는다),
 * 화면은 아래 훅으로 **노드 하나·현장 하나**씩 읽는다. 규칙:
 *   · 실측과 훈련 프레임을 **따로** 든다. 훈련 프레임이 15초 안에 들어왔으면 그것을, 아니면
 *     실측을 보여 준다 — 시뮬레이터가 RI 를 눈에 보이게 움직이되, 끝나면 곧 실측으로 돌아온다.
 *   · 180초(RISK_FRESH_MS) 넘게 새 프레임이 없으면 `stale` — 화면은 회색 「갱신 없음」으로
 *     그리고 현장 최고값에서 뺀다. 매일 밤 LTE 끊김(00:20~05:50) 동안 멈춘 0.05 가 「정상」
 *     초록으로 남아 있으면 화면이 「안전」이라고 거짓말하는 것이다.
 *   · 소켓·폴링·판독은 **250ms 마다 한 번에** 반영한다(ControlCenterPage `LIVE_FLUSH_MS` 와 같은
 *     까닭 — 판독 하나마다 스토어를 바꾸면 노드 수 × 주기만큼 화면이 다시 그려진다).
 *   · 24시간 아무것도 안 온 노드는 버린다.
 *
 * ⛔ zustand 5 는 셀렉터 결과를 `===` 로 비교한다. 셀렉터에서 새 객체·배열을 만들면
 *    「Maximum update depth」로 화면이 멈춘다. 그래서 파생값(노드·현장 뷰)은 **스토어 안에서**
 *    반영·시계 틱 때 계산해 두고, 값이 그대로면 같은 참조를 유지한다. 훅은 Map 에서 꺼내기만 한다.
 *    화면 쪽에서 더 가공할 때는 꺼낸 참조를 useMemo 의 의존성으로 쓴다.
 */

/** 훈련 프레임을 실측보다 앞세우는 최대 나이 — 이보다 오래되면 실측으로 돌아온다. */
export const DRILL_FRESH_MS = 15_000;
/** 반영 묶음 간격(ms). */
export const RISK_FLUSH_MS = 250;
/** 노드별 RI 이력 길이(소켓 프레임만) — 60초 스파크라인 재료(프레임 2~5초 간격). */
export const RISK_HISTORY_LEN = 30;
/** 폴링 프레임이 기존 프레임을 바꾸려면 이만큼은 더 새로워야 한다(같은 표본의 지연 차 흡수). */
const SEED_NEWER_MS = 1500;
/** 이만큼 아무것도 안 온 노드는 버린다. */
const IDLE_PRUNE_MS = 24 * 3600 * 1000;
/** 현장 최고 노드 이름을 바꾸려면 새 노드가 이만큼은 더 높아야 한다(RI 흔들림에 이름이 튀지 않게). */
const HEADLINE_HYST = 0.005;
/** 신선도·훈련 전환을 다시 판정하는 시계 간격. */
const CLOCK_MS = 1000;
/** 버리기 검사 간격(시계 틱 수). */
const PRUNE_EVERY_TICKS = 60;

/** 엔진 프레임 한 장 — `risk.updated` 푸시 또는 `latest` 폴링 한 줄을 같은 모양으로. */
export interface NodeRiskFrame {
  nodeId: number;
  siteId: number;
  /** 동적 RI(`ri_dynamic`) — 화면이 「위험도」라고 부르는 값. */
  ri: number;
  instant: number;
  /** 60초 창 평균. */
  cumulative: number;
  /** 60초 창 기울기×60, 0..1(엔진은 음수를 내지 않는다). */
  trend: number;
  level: Level;
  /** 가스별 BetP(0~1). 키는 엔진 표시 코드. */
  contributors: Readonly<Record<string, number>>;
  /** 가스별 풀이 — 백엔드 개편(P5) 이후에만. */
  gases?: Readonly<Record<string, RiskGasDetail>> | undefined;
  /** 복합 가스 가중 사유(예: `synergy(CO+H2S)×1.5`). 폴백 폴링에는 없다. */
  synergy: readonly string[];
  conflictK: number | null;
  /**
   * 이 프레임이 채점된 시각(브라우저 시계, ms). 소켓은 **받은 순간**(채점 직후 방송되므로),
   * 폴링은 「받은 순간 − 서버가 잰 나이」다 — 브라우저·서버 시계 차가 신선도에 끼지 않는다.
   */
  at: number;
  isDrill: boolean;
  source: "ws" | "poll";
}

/** 화면이 그리는 노드 위험도 — 실측·훈련 중 고른 프레임 + 신선도. */
export interface NodeRiskView extends NodeRiskFrame {
  /** 마지막 프레임이 RISK_FRESH_MS 보다 오래됨 — 회색 「갱신 없음」, 현장 최고값에서 제외. */
  stale: boolean;
}

/** 현장 헤드라인 — 신선한 노드 중 최고 RI. */
export interface SiteRiskView {
  /** 신선한 노드의 최고 RI. 신선한 노드가 없으면 null(= 「갱신 없음」, 절대 「정상」이 아님). */
  ri: number | null;
  level: Level | null;
  /** 최고 노드(RI 가 거의 같으면 이전 최고 노드를 유지 · stale 이면 가장 최근에 보고한 노드). */
  nodeId: number | null;
  /** 최고 노드의 최고 가스(정규화 코드). */
  topGas: string | null;
  trend: number | null;
  /** 최고 노드 프레임 시각(stale 이면 현장에서 가장 최근 프레임 시각). */
  at: number | null;
  /** 헤드라인이 훈련 프레임인가 — 「훈련」 표시. */
  isDrill: boolean;
  /** 신선한 노드가 하나도 없다. */
  stale: boolean;
  /** 현장의 어느 노드든 마지막 프레임이 들어온 시각 — 「실시간」 점을 한 번 깜박이는 재료. */
  updatedAt: number;
}

/** 소켓 판독 한 칸(노드·가스별). */
export interface LiveGasReading {
  value: number;
  /** 측정 시각(서버 `ts`, ms) — 폴링 행 `time` 과 같은 시계라 「어느 쪽이 더 새 값인가」를 비교할 수 있다. */
  at: number;
  /** 이 화면이 받은 시각(브라우저 시계) — 신선도(isPushStale)는 이것으로 잰다. */
  rx: number;
}

interface LiveRiskState {
  /** 노드별 실측 프레임(원본). */
  real: ReadonlyMap<number, NodeRiskFrame>;
  /** 노드별 훈련 프레임(원본). */
  drill: ReadonlyMap<number, NodeRiskFrame>;
  /** 노드별 화면용 뷰(파생) — 값이 그대로면 같은 참조. */
  nodes: ReadonlyMap<number, NodeRiskView>;
  /** 현장별 헤드라인(파생) — 값이 그대로면 같은 참조. */
  sites: ReadonlyMap<number, SiteRiskView>;
  /** 노드별 최근 RI(소켓 프레임만, 화면에 보이는 쪽) — 오래된 것 → 새 것. */
  history: ReadonlyMap<number, readonly number[]>;
  /** 노드별 최신 소켓 판독(가스 코드 정규화). */
  live: ReadonlyMap<number, ReadonlyMap<string, LiveGasReading>>;
  /** `risk.updated` 푸시 한 건. */
  pushRisk: (siteId: number, payload: RiskUpdatedPayload) => void;
  /** 폴링 프레임 — 같은 노드·같은 구분(실측/훈련)에 더 새 프레임이 있으면 버린다. */
  seedRisk: (siteId: number, frames: readonly NodeRiskFrame[]) => void;
  /** `sensor.reading` 푸시 한 건. `at` = 측정 시각, `rx` = 받은 시각(기본: 지금). */
  pushReading: (nodeId: number, gases: Readonly<Record<string, number>>, at: number, rx?: number) => void;
}

/* ───────────────────────── 변환 ───────────────────────── */

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

const clampLevel = (v: unknown, ri: number): Level => {
  const n = num(v);
  return n != null && n >= 0 && n <= 3 ? (Math.trunc(n) as Level) : riToLevel(ri);
};

/** `contributors` 는 스키마상 unknown — 숫자 칸만 남긴다. */
function cleanContributors(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = num(v);
    if (n != null) out[k] = n;
  }
  return out;
}

const EMPTY_REASONS: readonly string[] = Object.freeze([]);

/** `risk.updated` 페이로드 → 프레임. 필수 값이 없으면 null(버린다). */
export function frameFromPayload(siteId: number, p: RiskUpdatedPayload | null | undefined, at: number): NodeRiskFrame | null {
  if (!p || num(p.node_id) == null) return null;
  const ri = num(p.ri_dynamic) ?? num(p.ri_instant);
  if (ri == null) return null;
  return {
    nodeId: p.node_id,
    siteId,
    ri,
    instant: num(p.ri_instant) ?? ri,
    cumulative: num(p.ri_cumulative) ?? ri,
    trend: num(p.ri_trend) ?? 0,
    level: clampLevel(p.level, ri),
    contributors: cleanContributors(p.contributors),
    gases: p.gases ?? undefined,
    synergy: Array.isArray(p.synergy_reasons) ? p.synergy_reasons : EMPTY_REASONS,
    conflictK: num(p.conflict_k),
    at,
    isDrill: p.is_drill === true,
    source: "ws",
  };
}

/**
 * `latest` 응답 → 프레임들. 프레임 시각 = 받은 시각 − 서버가 잰 나이(`age_s`) — 브라우저 시계로
 * 옮겨 두므로 소켓 프레임과 「어느 쪽이 더 새 것인가」를 그대로 비교할 수 있다.
 */
export function framesFromLatest(siteId: number, latest: RiskLatest, fetchedAt: number): NodeRiskFrame[] {
  const out: NodeRiskFrame[] = [];
  for (const n of latest.nodes ?? []) {
    const nodeId = num(n.node_id);
    const ri = num(n.ri_dynamic) ?? num(n.ri_instant);
    if (nodeId == null || ri == null) continue;
    let ageMs = num(n.age_s) != null ? Math.max(0, n.age_s) * 1000 : null;
    if (ageMs == null) {
      const t = Date.parse(n.time);
      ageMs = Number.isFinite(t) ? Math.max(0, fetchedAt - t) : RISK_FRESH_MS + 1;
    }
    out.push({
      nodeId,
      siteId,
      ri,
      instant: num(n.ri_instant) ?? ri,
      cumulative: num(n.ri_cumulative) ?? ri,
      trend: num(n.ri_trend) ?? 0,
      level: clampLevel(n.level, ri),
      contributors: cleanContributors(n.contributors),
      gases: n.gases ?? undefined,
      synergy: Array.isArray(n.synergy_reasons) ? n.synergy_reasons : EMPTY_REASONS,
      conflictK: num(n.conflict_k),
      at: fetchedAt - ageMs,
      isDrill: n.is_drill ?? (latest.drill === true),
      source: "poll",
    });
  }
  return out;
}

/* ───────────────────────── 묶음 반영 ───────────────────────── */

type PendingOp =
  | { kind: "risk"; frame: NodeRiskFrame }
  | { kind: "seed"; frame: NodeRiskFrame }
  | { kind: "reading"; nodeId: number; gases: [string, LiveGasReading][] };

let queue: PendingOp[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let clockTimer: ReturnType<typeof setInterval> | null = null;
let ticks = 0;
/** 뷰 → 그 뷰를 만든 원본 프레임. 같은 프레임·같은 신선도면 뷰 참조를 재사용한다. */
const srcOf = new WeakMap<NodeRiskView, NodeRiskFrame>();

const EMPTY_HISTORY: readonly number[] = Object.freeze([]);
const EMPTY_LIVE: ReadonlyMap<string, LiveGasReading> = new Map();

function enqueue(op: PendingOp) {
  queue.push(op);
  if (flushTimer != null) return;
  flushTimer = setTimeout(flush, RISK_FLUSH_MS);
}

/** 이 노드에 지금 앞세울 훈련 프레임이 있나. */
const drillLive = (d: NodeRiskFrame | undefined, now: number) => d != null && now - d.at < DRILL_FRESH_MS;

function flush() {
  flushTimer = null;
  const ops = queue;
  queue = [];
  if (ops.length === 0) return;
  const now = Date.now();
  const s = useLiveRiskStore.getState();

  // 바뀌는 Map 만 복제한다(쓰기 시 복사).
  let real: Map<number, NodeRiskFrame> | null = null;
  let drill: Map<number, NodeRiskFrame> | null = null;
  let history: Map<number, readonly number[]> | null = null;
  let live: Map<number, ReadonlyMap<string, LiveGasReading>> | null = null;
  const liveTouched = new Map<number, Map<string, LiveGasReading>>();

  for (const op of ops) {
    if (op.kind === "reading") {
      let cur = liveTouched.get(op.nodeId);
      if (!cur) {
        cur = new Map(s.live.get(op.nodeId) ?? EMPTY_LIVE);
        liveTouched.set(op.nodeId, cur);
      }
      for (const [g, rec] of op.gases) {
        const prev = cur.get(g);
        // 늦게 도착한 옛 측정값(재전송 백로그)이 새 값을 덮지 않게.
        if (!prev || rec.at >= prev.at) cur.set(g, rec);
      }
      continue;
    }
    const f = op.frame;
    const target = f.isDrill ? (drill ??= new Map(s.drill)) : (real ??= new Map(s.real));
    const prev = target.get(f.nodeId);
    // 폴링은 **분명히 더 새 것**만 — 같은 표본을 소켓과 폴링이 각각 조금 다른 시각으로 옮겨
    // 오므로(지연 차), 여유 없이 비교하면 30초마다 같은 값이 가스별 풀이 없는 폴링 프레임으로
    // 바뀌어 화면만 다시 그린다.
    if (op.kind === "seed" && prev && f.at <= prev.at + SEED_NEWER_MS) continue;
    target.set(f.nodeId, f);
    if (op.kind === "risk") {
      // 이력은 **화면에 보이는 쪽**만 쌓는다 — 훈련 중에는 훈련 값, 아니면 실측.
      // 두 흐름을 섞으면 스파크라인이 실측 0.05 와 훈련 0.8 사이를 톱니처럼 오간다.
      const d = (drill ?? s.drill).get(f.nodeId);
      if (f.isDrill || !drillLive(d, now)) {
        history ??= new Map(s.history);
        const h = history.get(f.nodeId) ?? EMPTY_HISTORY;
        const next = h.length >= RISK_HISTORY_LEN ? h.slice(h.length - RISK_HISTORY_LEN + 1) : h.slice();
        next.push(f.ri);
        history.set(f.nodeId, next);
      }
    }
  }
  if (liveTouched.size > 0) {
    live = new Map(s.live);
    for (const [nid, m] of liveTouched) live.set(nid, m);
  }

  const patch: Partial<LiveRiskState> = {};
  if (real) patch.real = real;
  if (drill) patch.drill = drill;
  if (history) patch.history = history;
  if (live) patch.live = live;
  Object.assign(patch, derive(real ?? s.real, drill ?? s.drill, s.nodes, s.sites, now));
  if (Object.keys(patch).length > 0) useLiveRiskStore.setState(patch);
  ensureClock();
}

/**
 * 노드·현장 뷰를 다시 계산한다. 값이 그대로인 항목은 이전 참조를 유지하고, 아무것도 안 바뀌면
 * 빈 객체를 돌려준다(= setState 하지 않음 → 구독자가 다시 그리지 않는다).
 */
function derive(
  real: ReadonlyMap<number, NodeRiskFrame>,
  drill: ReadonlyMap<number, NodeRiskFrame>,
  prevNodes: ReadonlyMap<number, NodeRiskView>,
  prevSites: ReadonlyMap<number, SiteRiskView>,
  now: number,
): Partial<Pick<LiveRiskState, "nodes" | "sites">> {
  const nodes = new Map<number, NodeRiskView>();
  let nodesChanged = false;
  const ids = new Set<number>([...real.keys(), ...drill.keys()]);
  for (const id of ids) {
    const d = drill.get(id);
    // 훈련 프레임이 15초 안이면 그것, 아니면 실측. 실측이 없으면 뷰 없음(= 「위험도 계산 전」) —
    // 끝난 훈련의 옛 값을 실측처럼 남겨 두지 않는다.
    const f = drillLive(d, now) ? d : real.get(id);
    if (!f) continue;
    const stale = now - f.at > RISK_FRESH_MS;
    const prev = prevNodes.get(id);
    if (prev && srcOf.get(prev) === f && prev.stale === stale) {
      nodes.set(id, prev);
      continue;
    }
    const view: NodeRiskView = { ...f, stale };
    srcOf.set(view, f);
    nodes.set(id, view);
    nodesChanged = true;
  }
  if (nodes.size !== prevNodes.size) nodesChanged = true;

  // 현장 헤드라인 — 신선한 노드 중 최고 RI. 신선한 노드가 없으면 stale.
  // 값(ri·level)은 언제나 **참 최댓값**이고, 이름 붙는 노드(nodeId)만 이전 것을 붙든다:
  // 노드들의 RI 가 거의 같을 때(예: 모두 0 ppm 이면 똑같다) 「같으면 더 최근」으로 고르면 프레임이
  // 올 때마다 최고 노드가 바뀌어 /risk 상세·차트가 몇 초마다 다른 노드로 튄다(09-29 교차 검토).
  const bySite = new Map<number, NodeRiskView[]>();
  for (const v of nodes.values()) {
    const arr = bySite.get(v.siteId);
    if (arr) arr.push(v);
    else bySite.set(v.siteId, [v]);
  }
  const sites = new Map<number, SiteRiskView>();
  let sitesChanged = false;
  for (const [siteId, views] of bySite) {
    const prevNodeId = prevSites.get(siteId)?.nodeId ?? null;
    let top: NodeRiskView | undefined;
    let held: NodeRiskView | undefined;
    let newest: NodeRiskView | undefined;
    let updatedAt = 0;
    for (const v of views) {
      if (v.at > updatedAt) updatedAt = v.at;
      if (!newest || v.at > newest.at) newest = v;
      if (v.stale) continue;
      if (v.nodeId === prevNodeId) held = v;
      // 같은 RI 면 노드 번호가 작은 쪽 — 도착 순서에 기대지 않는 고정 순서.
      if (!top || v.ri > top.ri || (v.ri === top.ri && v.nodeId < top.nodeId)) top = v;
    }
    // 이전 최고 노드가 아직 신선하고, 참 최고와 같은 등급·HEADLINE_HYST 안이면 이름을 그대로 둔다.
    const worst =
      top && held && held !== top && held.level >= top.level && top.ri - held.ri <= HEADLINE_HYST ? held : top;
    const next: SiteRiskView = worst && top
      ? {
          ri: top.ri,
          level: top.level,
          nodeId: worst.nodeId,
          topGas: topGasOf(worst),
          trend: worst.trend,
          at: worst.at,
          isDrill: top.isDrill,
          stale: false,
          updatedAt,
        }
      : {
          ri: null,
          level: null,
          nodeId: newest?.nodeId ?? null,
          topGas: null,
          trend: null,
          at: newest?.at ?? null,
          isDrill: false,
          stale: true,
          updatedAt,
        };
    const prev = prevSites.get(siteId);
    if (prev && sameSite(prev, next)) {
      sites.set(siteId, prev);
    } else {
      sites.set(siteId, next);
      sitesChanged = true;
    }
  }
  if (sites.size !== prevSites.size) sitesChanged = true;

  const out: Partial<Pick<LiveRiskState, "nodes" | "sites">> = {};
  if (nodesChanged) out.nodes = nodes;
  if (sitesChanged) out.sites = sites;
  return out;
}

function sameSite(a: SiteRiskView, b: SiteRiskView): boolean {
  return (
    a.ri === b.ri && a.level === b.level && a.nodeId === b.nodeId && a.topGas === b.topGas &&
    a.trend === b.trend && a.at === b.at && a.isDrill === b.isDrill && a.stale === b.stale &&
    a.updatedAt === b.updatedAt
  );
}

/** 프레임의 최고 가스 — 백엔드가 `top` 을 찍어 주면 그것, 아니면 `contributors` 최댓값. */
function topGasOf(f: NodeRiskFrame): string | null {
  if (f.gases) {
    for (const [code, g] of Object.entries(f.gases)) if (g?.top) return normGas(code);
  }
  return topContributors(f.contributors, 1)[0]?.gas ?? null;
}

/**
 * 1초 시계 — 새 프레임이 없어도 「훈련 15초 → 실측」「180초 → 갱신 없음」 전환은 일어나야 한다.
 * 뷰가 바뀌지 않는 틱은 setState 하지 않으므로 화면은 다시 그려지지 않는다.
 * 첫 반영 때 켜고, 버리기 뒤에 스토어가 비면 끈다.
 */
function ensureClock() {
  if (clockTimer != null) return;
  clockTimer = setInterval(tick, CLOCK_MS);
}

function tick() {
  const now = Date.now();
  const s = useLiveRiskStore.getState();
  ticks += 1;
  let real = s.real;
  let drill = s.drill;
  const patch: Partial<LiveRiskState> = {};
  if (ticks % PRUNE_EVERY_TICKS === 0) {
    const pruned = prune(s, now);
    Object.assign(patch, pruned);
    real = pruned.real ?? real;
    drill = pruned.drill ?? drill;
  }
  Object.assign(patch, derive(real, drill, s.nodes, s.sites, now));
  if (Object.keys(patch).length > 0) useLiveRiskStore.setState(patch);

  const after = useLiveRiskStore.getState();
  if (after.real.size === 0 && after.drill.size === 0 && after.live.size === 0 && queue.length === 0) {
    if (clockTimer != null) clearInterval(clockTimer);
    clockTimer = null;
  }
}

/** 24시간 아무것도 안 온 노드를 모든 Map 에서 뺀다. */
function prune(s: LiveRiskState, now: number): Partial<LiveRiskState> {
  const lastSeen = new Map<number, number>();
  const bump = (id: number, at: number) => {
    if ((lastSeen.get(id) ?? -Infinity) < at) lastSeen.set(id, at);
  };
  for (const f of s.real.values()) bump(f.nodeId, f.at);
  for (const f of s.drill.values()) bump(f.nodeId, f.at);
  for (const [id, m] of s.live) for (const r of m.values()) bump(id, r.rx);
  const idle = [...lastSeen].filter(([, at]) => now - at > IDLE_PRUNE_MS).map(([id]) => id);
  if (idle.length === 0) return {};
  const drop = <V,>(m: ReadonlyMap<number, V>): Map<number, V> | null => {
    if (!idle.some((id) => m.has(id))) return null;
    const next = new Map(m);
    for (const id of idle) next.delete(id);
    return next;
  };
  const out: Partial<LiveRiskState> = {};
  const real = drop(s.real);
  const drill = drop(s.drill);
  const history = drop(s.history);
  const live = drop(s.live);
  if (real) out.real = real;
  if (drill) out.drill = drill;
  if (history) out.history = history;
  if (live) out.live = live;
  return out;
}

/* ───────────────────────── 스토어 ───────────────────────── */

export const useLiveRiskStore = create<LiveRiskState>()(() => ({
  real: new Map(),
  drill: new Map(),
  nodes: new Map(),
  sites: new Map(),
  history: new Map(),
  live: new Map(),
  pushRisk: (siteId, payload) => {
    const f = frameFromPayload(siteId, payload, Date.now());
    if (f) enqueue({ kind: "risk", frame: f });
  },
  seedRisk: (siteId, frames) => {
    for (const f of frames) {
      // 다른 현장 프레임이 섞여 들어오지 않게 — 브리지는 현장마다 하나다.
      if (f.siteId === siteId) enqueue({ kind: "seed", frame: f });
    }
  },
  pushReading: (nodeId, gases, at, rx) => {
    const got = rx ?? Date.now();
    const rows: [string, LiveGasReading][] = [];
    for (const [g, v] of Object.entries(gases)) {
      const value = num(v);
      const code = normGas(g);
      if (value != null && code) rows.push([code, { value, at, rx: got }]);
    }
    if (rows.length > 0) enqueue({ kind: "reading", nodeId, gases: rows });
  },
}));

/* ───────────────────────── 훅 ─────────────────────────
 * 모두 스토어에 **이미 있는 참조**만 꺼낸다(새 객체를 만들지 않는다 — 위 ⛔ 참조). */

/** 노드 하나의 화면용 위험도. 프레임이 한 번도 없으면 null(= 「위험도 계산 전」). */
export function useNodeRisk(nodeId: number | null | undefined): NodeRiskView | null {
  return useLiveRiskStore((s) => (nodeId == null ? null : (s.nodes.get(nodeId) ?? null)));
}

/** 노드의 최근 RI(오래된 것 → 새 것, 최대 RISK_HISTORY_LEN) — 스파크라인용. 없으면 빈 배열(같은 참조). */
export function useNodeRiskHistory(nodeId: number | null | undefined): readonly number[] {
  return useLiveRiskStore((s) => (nodeId == null ? EMPTY_HISTORY : (s.history.get(nodeId) ?? EMPTY_HISTORY)));
}

/**
 * 노드의 최신 소켓 판독(가스 코드 정규화 → 값·측정 시각·받은 시각). 폴링 행(30초)보다 새면
 * 이것을 보여 준다 — 비교는 `at`(측정 시각), 신선도는 `rx`(받은 시각)로.
 */
export function useNodeLiveGases(nodeId: number | null | undefined): ReadonlyMap<string, LiveGasReading> {
  return useLiveRiskStore((s) => (nodeId == null ? EMPTY_LIVE : (s.live.get(nodeId) ?? EMPTY_LIVE)));
}

/**
 * 현장 헤드라인 — 신선한 노드 중 최고 RI 와 그 노드·최고 가스·추세.
 * 프레임이 하나도 없으면 null(호출 쪽이 폴링 카드 값 등으로 대신한다),
 * 모두 멈췄으면 `stale: true`·`ri: null`(회색 「갱신 없음」).
 */
export function useSiteRisk(siteId: number | null | undefined): SiteRiskView | null {
  return useLiveRiskStore((s) => (siteId == null ? null : (s.sites.get(siteId) ?? null)));
}
